import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Zap, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function AuthCallback() {
  const processed = useRef(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    api.post("/auth/session", { session_id: sid })
      .then(({ data }) => {
        login(data);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true });
      })
      .catch((e) => {
        toast.error(errMsg(e));
        window.history.replaceState(null, "", "/login");
        navigate("/login", { replace: true });
      });
  }, [login, navigate]);
  return <div className="min-h-screen flex items-center justify-center text-muted-foreground"><Loader2 className="animate-spin mr-2" /> Iniciando sesión…</div>;
}

export default function Login() {
  const [mode, setMode] = useState(new URLSearchParams(window.location.search).get("ref") ? "register" : "login");
  const [forgot, setForgot] = useState(false);
  const refCode = new URLSearchParams(window.location.search).get("ref") || "";
  const [form, setForm] = useState({ email: "", password: "", name: "", ref: refCode });
  const [busy, setBusy] = useState(false);
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { if (user) navigate(location.state?.from || "/", { replace: true }); }, [user, navigate, location]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/${mode}`, mode === "login" ? { email: form.email, password: form.password } : form);
      login(data);
      toast.success(mode === "login" ? `Hola, ${data.user.name}` : "Cuenta creada");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const forgotPwd = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/forgot-password", { email: form.email }); toast.success(data.message); setForgot(false); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1640720930833-e6c12967a15d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400')] bg-cover bg-center opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent" />
        <div className="relative flex items-center gap-2"><span className="h-10 w-10 rounded-xl bg-primary/20 text-primary flex items-center justify-center glow-cyan"><Zap /></span><span className="font-display font-bold text-xl">CubiMarket & Pay</span></div>
        <div className="relative">
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold tracking-tight leading-tight">Comida, compras y cripto<br /><span className="text-cyan-300">en una sola billetera.</span></h1>
          <p className="mt-4 text-muted-foreground max-w-md">Pide comida, compra y vende productos, intercambia USDT P2P y paga con tu saldo interno. Entregas verificadas con código QR.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md neon-card p-6 sm:p-8 fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-6"><span className="h-9 w-9 rounded-xl bg-primary/20 text-primary flex items-center justify-center"><Zap size={18} /></span><span className="font-display font-bold text-lg">CubiMarket & Pay</span></div>
          <h2 className="font-display text-2xl font-bold">{forgot ? "Recuperar contraseña" : mode === "login" ? "Inicia sesión" : "Crea tu cuenta"}</h2>
          <p className="text-sm text-muted-foreground mt-1">{forgot ? "Te enviaremos un enlace a tu correo" : mode === "login" ? "Bienvenido de nuevo" : "Es rápido y gratis"}</p>
          {forgot ? (
            <div className="mt-6 space-y-4">
              <div><Label htmlFor="femail">Correo</Label><Input id="femail" type="email" data-testid="forgot-email-input" className="h-12 mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
              <Button type="button" disabled={busy || !form.email} data-testid="forgot-submit-button" onClick={forgotPwd} className="w-full tap bg-primary text-primary-foreground hover:bg-cyan-400">{busy ? <Loader2 className="animate-spin" /> : "Enviar enlace"}</Button>
              <button type="button" data-testid="forgot-back" onClick={() => setForgot(false)} className="w-full text-sm text-muted-foreground hover:text-foreground">Volver</button>
            </div>
          ) : (<>
          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && (
              <div><Label htmlFor="name">Nombre</Label><Input id="name" data-testid="register-name-input" className="h-12 mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required minLength={2} /></div>
            )}
            {mode === "register" && (
              <div><Label htmlFor="ref">Código de referido (opcional)</Label><Input id="ref" data-testid="register-ref-input" className="h-12 mt-1 font-mono uppercase" placeholder="CM-XXXXXX" value={form.ref} onChange={(e) => setForm({ ...form, ref: e.target.value.toUpperCase() })} /></div>
            )}
            <div><Label htmlFor="email">Correo</Label><Input id="email" type="email" data-testid="auth-email-input" className="h-12 mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
            <div><Label htmlFor="password">Contraseña</Label><Input id="password" type="password" data-testid="auth-password-input" className="h-12 mt-1" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} /></div>
            <Button type="submit" disabled={busy} data-testid="auth-submit-button" className="w-full tap bg-primary text-primary-foreground hover:bg-cyan-400">
              {busy ? <Loader2 className="animate-spin" /> : mode === "login" ? "Entrar" : "Registrarme"}
            </Button>
            {mode === "login" && <button type="button" data-testid="forgot-password-link" onClick={() => setForgot(true)} className="w-full text-xs text-muted-foreground hover:text-foreground">¿Olvidaste tu contraseña?</button>}
          </form>
          <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />o<span className="h-px flex-1 bg-border" /></div>
          <Button type="button" variant="outline" onClick={google} data-testid="google-login-button" className="w-full tap bg-secondary/60 hover:bg-secondary">
            <svg width="18" height="18" viewBox="0 0 48 48" className="mr-2"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.8 6.1C12.3 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"/><path fill="#FBBC05" d="M10.4 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.1.8-4.6l-7.8-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.8-6.1z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.9 2.3-8.4 2.3-6.3 0-11.7-4.1-13.6-9.9l-7.8 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
            Continuar con Google
          </Button>
          <button type="button" data-testid="toggle-auth-mode" onClick={() => setMode(mode === "login" ? "register" : "login")} className="mt-6 w-full text-sm text-muted-foreground hover:text-foreground transition-colors">
            {mode === "login" ? "¿No tienes cuenta? Regístrate" : "¿Ya tienes cuenta? Inicia sesión"}
          </button>
          <Link to="/terminos" data-testid="login-terms-link" className="block mt-3 text-center text-xs text-muted-foreground hover:text-foreground">Términos y condiciones</Link>
          </>)}
        </div>
      </div>
    </div>
  );
}
